// Debug component to help troubleshoot profile editing issues
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contextapi/UserContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';

export const ProfileEditingDebug: React.FC = () => {
  const { user } = useUser();
  const { permissions } = useUserPermissions();
  const [testFormData, setTestFormData] = useState({
    name: '',
    fullName: '',
    email: ''
  });
  const [debugInfo, setDebugInfo] = useState<any>({});

  useEffect(() => {
    if (user) {
      const initialData = {
        name: user.user_metadata?.name || '',
        fullName: user.user_metadata?.full_name || '',
        email: user.email || ''
      };
      setTestFormData(initialData);
      
      setDebugInfo({
        userId: user.id,
        userEmail: user.email,
        userMetadata: user.user_metadata,
        canEditProfile: permissions?.canEditProfile,
        isGuest: permissions?.isGuest,
        timestamp: new Date().toISOString()
      });
    }
  }, [user, permissions]);

  const handleInputChange = (field: string, value: string) => {
    console.log(`[ProfileEditingDebug] Changing ${field} to:`, value);
    setTestFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testFormSubmission = () => {
    console.log('[ProfileEditingDebug] Form submission test:', testFormData);
    alert(`Form data: ${JSON.stringify(testFormData, null, 2)}`);
  };

  if (!user) {
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <h3 className="font-semibold text-red-800">Profile Editing Debug</h3>
        <p className="text-red-600">No user logged in</p>
      </div>
    );
  }

  return (
    <div className="p-6 border border-blue-200 rounded-lg bg-blue-50 space-y-4">
      <h3 className="text-lg font-semibold text-blue-800">Profile Editing Debug Tool</h3>
      
      {/* Debug Information */}
      <div className="bg-white p-4 rounded border">
        <h4 className="font-medium mb-2">Debug Information</h4>
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
          {JSON.stringify(debugInfo, null, 2)}
        </pre>
      </div>

      {/* Permission Check */}
      <div className="bg-white p-4 rounded border">
        <h4 className="font-medium mb-2">Permission Status</h4>
        <div className="space-y-1 text-sm">
          <div className={`flex items-center gap-2 ${permissions?.canEditProfile ? 'text-green-600' : 'text-red-600'}`}>
            <span>{permissions?.canEditProfile ? '✅' : '❌'}</span>
            <span>Can Edit Profile: {permissions?.canEditProfile ? 'Yes' : 'No'}</span>
          </div>
          <div className={`flex items-center gap-2 ${!permissions?.isGuest ? 'text-green-600' : 'text-red-600'}`}>
            <span>{!permissions?.isGuest ? '✅' : '❌'}</span>
            <span>Not Guest User: {!permissions?.isGuest ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* Test Form */}
      <div className="bg-white p-4 rounded border">
        <h4 className="font-medium mb-3">Test Profile Form</h4>
        <div className="space-y-3">
          {/* Email (disabled) */}
          <div>
            <label className="block text-sm font-medium mb-1">Email (Read-only)</label>
            <input
              type="email"
              value={testFormData.email}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
          </div>

          {/* Name field */}
          <div>
            <label className="block text-sm font-medium mb-1">Name Field</label>
            <input
              type="text"
              value={testFormData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Value: "{testFormData.name}" | Length: {testFormData.name.length}
            </p>
          </div>

          {/* Full Name field */}
          <div>
            <label className="block text-sm font-medium mb-1">Full Name Field</label>
            <input
              type="text"
              value={testFormData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              placeholder="Enter full name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Value: "{testFormData.fullName}" | Length: {testFormData.fullName.length}
            </p>
          </div>

          {/* Test Button */}
          <button
            onClick={testFormSubmission}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Test Form Submission
          </button>
        </div>
      </div>

      {/* Current Form State */}
      <div className="bg-white p-4 rounded border">
        <h4 className="font-medium mb-2">Current Form State</h4>
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
          {JSON.stringify(testFormData, null, 2)}
        </pre>
      </div>

      {/* Instructions */}
      <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
        <h4 className="font-medium text-yellow-800 mb-2">Testing Instructions</h4>
        <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
          <li>Try typing in both the "Name Field" and "Full Name Field" above</li>
          <li>Check that the values update in real-time in the "Current Form State" section</li>
          <li>Verify that permissions show as "Can Edit Profile: Yes"</li>
          <li>Click "Test Form Submission" to see the current form data</li>
          <li>If any field doesn't update, there's a form binding issue</li>
        </ol>
      </div>

      {/* Browser Console Logs */}
      <div className="bg-gray-50 p-4 rounded border">
        <h4 className="font-medium mb-2">Console Logging</h4>
        <p className="text-sm text-gray-600">
          Check your browser's developer console for detailed logging when you type in the fields above.
          Look for messages starting with "[ProfileEditingDebug]".
        </p>
      </div>
    </div>
  );
};

export default ProfileEditingDebug;
